'use client';


import { Box } from "../Layout/Box/Box.js";
import { MotionBox } from "../MotionBox/MotionBox.js";
import { chakra, useStyleConfig } from "@chakra-ui/react";
import { c } from "react-compiler-runtime";
import React from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { Focusable, OverlayArrow, Tooltip, TooltipTrigger } from "react-aria-components";

//#region src/components/Tooltip/Tooltip.tsx
const scale = {
	exit: {
		scale: .85,
		opacity: 0,
		transition: {
			opacity: {
				duration: .15,
				ease: "easeInOut"
			},
			scale: {
				duration: .2,
				ease: "easeInOut"
			}
		}
	},
	enter: {
		scale: 1,
		opacity: 1,
		transition: {
			opacity: {
				ease: "easeOut",
				duration: .2
			},
			scale: {
				duration: .2,
				ease: [
					.175,
					.885,
					.4,
					1.1
				]
			}
		}
	}
};
const ChakraAriaTooltip = chakra(Tooltip);
const Svg = chakra("svg");
const Span = chakra("span");
const ChakraToAriaPlacement = (placement) => {
	const temp = placement?.split("-");
	if (temp) if (temp?.length === 2) {
		temp[0] = temp[0].replace("auto", "bottom");
		temp[1] = temp[1].replace("auto", "start");
	} else temp[0] = temp[0].replace("auto", "start");
	return temp?.join(" ");
};
const TooltipTriggerWrapper = React.forwardRef((props, ref) => {
	const $ = c(3);
	let t0;
	if ($[0] !== props || $[1] !== ref) {
		t0 = /* @__PURE__ */ jsx(Span, {
			...props,
			ref,
			display: "inline-block",
			tabIndex: 0
		});
		$[0] = props;
		$[1] = ref;
		$[2] = t0;
	} else t0 = $[2];
	return t0;
});
/**
* A tooltip is a brief, informative message that appears when a user interacts with an element. Tooltips are usually initiated in one of two ways: through a mouse-hover gesture or through a keyboard-hover gesture.
*/
const Tooltip$1 = (t0) => {
	const $ = c(57);
	let ariaLabel;
	let children;
	let closeDelay;
	let defaultIsOpen;
	let gutter;
	let hasArrow;
	let isOpen;
	let label;
	let offset;
	let onClose;
	let onOpen;
	let placement;
	let props;
	let t1;
	let t2;
	let t3;
	if ($[0] !== t0) {
		({children, openDelay: t1, closeDelay, onOpen, onClose, defaultIsOpen, isOpen, label, hasArrow, placement, "aria-label": ariaLabel, gutter, offset, shouldWrapChildren: t2, isDisabled: t3, ...props} = t0);
		$[0] = t0;
		$[1] = ariaLabel;
		$[2] = children;
		$[3] = closeDelay;
		$[4] = defaultIsOpen;
		$[5] = gutter;
		$[6] = hasArrow;
		$[7] = isOpen;
		$[8] = label;
		$[9] = offset;
		$[10] = onClose;
		$[11] = onOpen;
		$[12] = placement;
		$[13] = props;
		$[14] = t1;
		$[15] = t2;
		$[16] = t3;
	} else {
		ariaLabel = $[1];
		children = $[2];
		closeDelay = $[3];
		defaultIsOpen = $[4];
		gutter = $[5];
		hasArrow = $[6];
		isOpen = $[7];
		label = $[8];
		offset = $[9];
		onClose = $[10];
		onOpen = $[11];
		placement = $[12];
		props = $[13];
		t1 = $[14];
		t2 = $[15];
		t3 = $[16];
	}
	const openDelay = t1 === void 0 ? 500 : t1;
	const shouldWrapChildren = t2 === void 0 ? true : t2;
	const isDisabled = t3 === void 0 ? false : t3;
	let t4;
	let t5;
	if ($[17] !== placement) {
		t5 = ChakraToAriaPlacement(placement);
		$[17] = placement;
		$[18] = t5;
	} else t5 = $[18];
	t4 = t5;
	const mappedPlacement = t4;
	const styles = useStyleConfig("Tooltip");
	let t6;
	if ($[19] !== onClose || $[20] !== onOpen) {
		t6 = (isOpen_0) => {
			if (isOpen_0) onOpen?.();
			else onClose?.();
		};
		$[19] = onClose;
		$[20] = onOpen;
		$[21] = t6;
	} else t6 = $[21];
	const t7 = isDisabled || !label;
	let t8;
	if ($[22] !== children || $[23] !== shouldWrapChildren) {
		t8 = shouldWrapChildren ? /* @__PURE__ */ jsx(Focusable, { children: /* @__PURE__ */ jsx(TooltipTriggerWrapper, { children }) }) : /* @__PURE__ */ jsx(Focusable, { children });
		$[22] = children;
		$[23] = shouldWrapChildren;
		$[24] = t8;
	} else t8 = $[24];
	const t9 = offset ?? gutter;
	const t10 = props.bg ?? "neutral.1400";
	let t11;
	if ($[25] !== t10) {
		t11 = { fill: t10 };
		$[25] = t10;
		$[26] = t11;
	} else t11 = $[26];
	let t12;
	if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
		t12 = {
			marginBottom: "8px",
			"--origin": "translateY(4px)"
		};
		$[27] = t12;
	} else t12 = $[27];
	let t13;
	if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
		t13 = {
			marginTop: "8px",
			"--origin": "translateY(-4px)",
			"& .react-aria-OverlayArrow svg": { transform: "rotate(180deg)" }
		};
		$[28] = t13;
	} else t13 = $[28];
	let t14;
	if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
		t14 = {
			marginLeft: "8px",
			"--origin": "translateX(-4px)",
			"& .react-aria-OverlayArrow svg": { transform: "rotate(90deg)" }
		};
		$[29] = t14;
	} else t14 = $[29];
	let t15;
	if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
		t15 = {
			marginRight: "8px",
			"--origin": "translateX(4px)",
			"& .react-aria-OverlayArrow svg": { transform: "rotate(-90deg)" }
		};
		$[30] = t15;
	} else t15 = $[30];
	let t16;
	if ($[31] !== t11) {
		t16 = {
			"& .react-aria-OverlayArrow svg": t11,
			"&[data-placement=top]": t12,
			"&[data-placement=bottom]": t13,
			"&[data-placement=right]": t14,
			"&[data-placement=left]": t15
		};
		$[31] = t11;
		$[32] = t16;
	} else t16 = $[32];
	let t17;
	if ($[33] !== hasArrow) {
		t17 = hasArrow ? /* @__PURE__ */ jsx(OverlayArrow, { children: /* @__PURE__ */ jsx(Svg, {
			width: 3,
			height: 3,
			viewBox: "0 0 8 8",
			children: /* @__PURE__ */ jsx("path", { d: "M0 0 L4 4 L8 0" })
		}) }) : null;
		$[33] = hasArrow;
		$[34] = t17;
	} else t17 = $[34];
	let t18;
	if ($[35] !== label || $[36] !== props || $[37] !== styles) {
		t18 = /* @__PURE__ */ jsx(Box, {
			__css: styles,
			...props,
			children: label
		});
		$[35] = label;
		$[36] = props;
		$[37] = styles;
		$[38] = t18;
	} else t18 = $[38];
	let t19;
	if ($[39] !== t17 || $[40] !== t18) {
		t19 = /* @__PURE__ */ jsxs(MotionBox, {
			variants: scale,
			initial: "exit",
			animate: "enter",
			exit: "exit",
			children: [t17, t18]
		});
		$[39] = t17;
		$[40] = t18;
		$[41] = t19;
	} else t19 = $[41];
	let t20;
	if ($[42] !== ariaLabel || $[43] !== mappedPlacement || $[44] !== t16 || $[45] !== t19 || $[46] !== t9) {
		t20 = /* @__PURE__ */ jsx(ChakraAriaTooltip, {
			"aria-label": ariaLabel,
			placement: mappedPlacement,
			offset: t9,
			sx: t16,
			children: t19
		});
		$[42] = ariaLabel;
		$[43] = mappedPlacement;
		$[44] = t16;
		$[45] = t19;
		$[46] = t9;
		$[47] = t20;
	} else t20 = $[47];
	let t21;
	if ($[48] !== closeDelay || $[49] !== defaultIsOpen || $[50] !== isOpen || $[51] !== openDelay || $[52] !== t20 || $[53] !== t6 || $[54] !== t7 || $[55] !== t8) {
		t21 = /* @__PURE__ */ jsxs(TooltipTrigger, {
			delay: openDelay,
			closeDelay,
			isOpen,
			onOpenChange: t6,
			defaultOpen: defaultIsOpen,
			isDisabled: t7,
			children: [t8, t20]
		});
		$[48] = closeDelay;
		$[49] = defaultIsOpen;
		$[50] = isOpen;
		$[51] = openDelay;
		$[52] = t20;
		$[53] = t6;
		$[54] = t7;
		$[55] = t8;
		$[56] = t21;
	} else t21 = $[56];
	return t21;
};

//#endregion
export { Tooltip$1 as Tooltip };
//# sourceMappingURL=Tooltip.js.map